import React, { useMemo } from 'react';
import {
  Banner,
  Button,
  CondensedHeader,
  CondensedMedium,
  Dropdown,
  Form,
  Grid,
  GridColumn,
  GridRow,
  Gutter,
  Layout,
  LayoutItem,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Toggle,
} from '@axiom/ui';
import { UserCustomTalentAlertsConst } from '@axiom/const';
import type { Candidate, UserCustomTalentAlert } from '@axiom/validation';

import { UserAlertsApi } from '../../api/user-alerts-api';

import {
  AvailabilityAlertModalSchema,
  type AvailabilityAlertModalType,
} from './availability-alert-modal-schema';

const MINIMUM_HOURS_OPTIONS = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '15', value: 15 },
  { label: '20', value: 20 },
  { label: '25', value: 25 },
  { label: '30', value: 30 },
  { label: '35', value: 35 },
  { label: '40', value: 40 },
  { label: '45', value: 45 },
  { label: '50', value: 50 },
];

export type AvailabilityAlertModalProps = {
  onClose: () => void;
  candidate: Candidate;
  existingAlerts?: UserCustomTalentAlert[];
};

export const AvailabilityAlertModal = ({
  onClose,
  candidate,
  existingAlerts,
}: AvailabilityAlertModalProps) => {
  const isEditMode = !!existingAlerts?.length;

  const availabilityAlert = existingAlerts?.find(
    a => a.alertType === UserCustomTalentAlertsConst.AlertType.Availability
  );
  const rollOffAlert = existingAlerts?.find(
    a => a.alertType === UserCustomTalentAlertsConst.AlertType.RollOff
  );

  const getAvailabilityDisplay = () => {
    const hours = candidate.weeklyAvailability ?? '--';
    return `${candidate.calculatedFirstName} currently has ${hours} hours/week available.`;
  };

  const initialValues = useMemo(
    () => ({
      availabilityAlertActive: availabilityAlert?.alertActive ?? false,
      minAvailability: availabilityAlert?.minAvailability ?? 5,
      rollOffAlertActive: rollOffAlert?.alertActive ?? true,
    }),
    [availabilityAlert, rollOffAlert]
  );

  const handleSubmit = async (values: AvailabilityAlertModalType) => {
    if (isEditMode) {
      const updates = [];
      if (availabilityAlert) {
        updates.push(
          UserAlertsApi.updateCustomTalentAlert(availabilityAlert.id, {
            candidateId: candidate.id,
            alertType: UserCustomTalentAlertsConst.AlertType.Availability,
            alertActive: values.availabilityAlertActive,
            minAvailability: values.availabilityAlertActive
              ? values.minAvailability
              : availabilityAlert.minAvailability,
          })
        );
      }
      if (rollOffAlert) {
        updates.push(
          UserAlertsApi.updateCustomTalentAlert(rollOffAlert.id, {
            candidateId: candidate.id,
            alertType: UserCustomTalentAlertsConst.AlertType.RollOff,
            alertActive: values.rollOffAlertActive,
            minAvailability: null,
          })
        );
      }
      await Promise.all(updates);
    } else {
      await Promise.all([
        UserAlertsApi.createCustomTalentAlert({
          candidateId: candidate.id,
          alertType: UserCustomTalentAlertsConst.AlertType.Availability,
          alertActive: values.availabilityAlertActive,
          minAvailability: values.availabilityAlertActive
            ? values.minAvailability
            : 5,
        }),
        UserAlertsApi.createCustomTalentAlert({
          candidateId: candidate.id,
          alertType: UserCustomTalentAlertsConst.AlertType.RollOff,
          alertActive: values.rollOffAlertActive,
          minAvailability: null,
        }),
      ]);
    }
    await UserAlertsApi.refreshUserAlerts();
    onClose();
  };

  return (
    <Form
      initialValues={initialValues}
      name="AVAILABILITY_ALERT_FORM"
      schema={AvailabilityAlertModalSchema}
      onSubmit={async (_changedData, _actions, values) => handleSubmit(values)}
    >
      {({ fireSubmit, values }) => (
        <Modal name="AvailabilityAlertModal">
          <ModalHeader
            onClose={() => {
              onClose();
            }}
          >
            {isEditMode ? 'Edit' : 'Create'} availability alert -{' '}
            {candidate.calculatedFirstName}
          </ModalHeader>
          <ModalSection name="AVAILABILITY_ALERT_SECTION" disableScrolling>
            <Banner type="info" impact="low" name="AVAILABILITY_BANNER">
              <CondensedHeader>{getAvailabilityDisplay()}</CondensedHeader>
            </Banner>
            <Gutter bottom="24px">
              <Gutter top="24px">
                <Layout position="middle">
                  <LayoutItem fluid>
                    <CondensedHeader>Availability increase</CondensedHeader>
                    <Gutter top="8px">
                      <CondensedMedium>
                        Email me when their weekly availability increases to a
                        minimum number of hours.
                      </CondensedMedium>
                    </Gutter>
                    <Gutter top="8px">
                      <Toggle name="availabilityAlertActive" />
                    </Gutter>
                  </LayoutItem>
                </Layout>
              </Gutter>
              {values.availabilityAlertActive ? (
                <Gutter top="24px">
                  <Grid columns={12}>
                    <GridRow>
                      <GridColumn width={3}>
                        <Dropdown
                          name="minAvailability"
                          label="Minimum hours"
                          options={MINIMUM_HOURS_OPTIONS}
                          displayKey="label"
                          valueKey="value"
                        />
                      </GridColumn>
                    </GridRow>
                  </Grid>
                </Gutter>
              ) : null}
            </Gutter>
            <Gutter top="24px">
              <Layout position="middle">
                <LayoutItem fluid>
                  <CondensedHeader>Engagement end date</CondensedHeader>
                  <Gutter top="8px">
                    <CondensedMedium>
                      Email me when their confirmed end date is within 2 weeks.
                    </CondensedMedium>
                  </Gutter>
                  <Gutter top="8px">
                    <Toggle name="rollOffAlertActive" />
                  </Gutter>
                </LayoutItem>
              </Layout>
            </Gutter>
          </ModalSection>
          <ModalFooter>
            <Button
              name="CANCEL_BUTTON"
              onClick={() => {
                onClose();
              }}
              variation="outline"
            >
              Cancel
            </Button>
            <Button name="SUBMIT_BUTTON" onClick={fireSubmit}>
              {isEditMode ? 'Save' : 'Create Alert'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </Form>
  );
};
